import {
	AlertBar,
	Anchor,
	FormattedText,
	ToolbarAction,
	useConfirmationDialog
} from "client/Components";
import { UsersAndUserGroupsDataModel, UserGroupOverviewModel } from "client/Api/UserGroupsManagement/userGroupsManagement";
import {
	UserAndUserGroupOverviews,
	UserAndUserGroupSelection
} from "client/Features/Users/UserAndUserGroupCollection/UserAndUserGroupOverviews";
import { ActiveTab } from "client/Features/Users/UserAndUserGroupTabSelector/UserAndUserGroupTabSelector";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUnmountedCancellation,
	useWorkspaceMediaQuery
} from "client/Hooks";
import {
	downloadAsFile,
	formatDataAsCsv,
	isNil
} from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import {
	ActionKind,
	buildActionsForTopBar,
	deselectAllTitle
} from "client_legacy/ndaw/actions";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { SettingsPage } from "../../SettingsPage";
import {
	activateGroups,
	activateUsers,
	CanManageUserActivateStatusKind,
	deactivateGroups,
	deactivateUsers,
	getPageModel,
	UserLicensingPageModel
} from "client/Api/UserLicensingManagement/userLicensingManagement";
import styles from "./Page.module.less";
import { useActiveSettingsMenuItem } from "../../SidebarMenu/sidebarMenuSelectionStore";
import { UserGridItem } from "client/Api/UserManagement/userManagement";
import { handlePageDataLoadingError } from "client/Features/ErrorPage/errorStore";
import { SettingsPageKind } from "client/Api/SettingsLayout/settingsLayout";
import { useManageUserLicensesDialog } from "./ManageUserLicensesDialog";
import { getUserLicenseName } from "client/Features/FeaturePackages/misc";
import { reloadCurrentPage } from "client_legacy/ndaw/navigator";
import { pushMessage } from "client/Features/Alerts/pageReloadAlerts";
import { SetUserLicenseAudience, UserLicense } from "client/Api/FeaturePackages/featurePackageManagement";
import { AiAssistantFabSpacing } from "client/Features/ChatAssistant/AiAssistantFabSpacing";

export const BILLING_SUPPORT_ARTICLE_URL = "https://support.workspace365.net/hc/en-us/articles/360012676120-Billing-Subscription";

export const Page = (): ReactElement => {
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const [wasAlertDismissed, setWasAlertDismissed] = useState<boolean>(false);
	const [canManageUserActiveStatusInWorkspace, setCanManageUserActiveStatusInWorkspace] = useState<CanManageUserActivateStatusKind>(CanManageUserActivateStatusKind.NotAllowed);
	const [usersAndUserGroupsData, setUsersAndUserGroupsData] = useState<UsersAndUserGroupsDataModel>(DEFAULT_USERS_AND_USER_GROUPS_DATA);
	const [activeTab, setActiveTab] = useState<ActiveTab>(ActiveTab.User);
	const [selection, setSelection] = useState<UserAndUserGroupSelection>({
		users: [],
		userGroups: []
	});

	const globalCancellationToken: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const [confirmationDialog, showConfirmationDialog] = useConfirmationDialog();
	const [changeUserLicensesDialog, openChangeUserLicensesDialog] = useManageUserLicensesDialog();
	useActiveSettingsMenuItem(SettingsPageKind.Licensing);

	useEffect(
		(): EffectWithDestructorResult => {
			async function fetchData(signal: AbortSignal): Promise<void> {
				setIsBusy(true);

				try {
					const model: UserLicensingPageModel = await getPageModel(signal)
						.catch(handlePageDataLoadingError);

					setCanManageUserActiveStatusInWorkspace(model.canManageUserActiveStatusInWorkspace);
					setUsersAndUserGroupsData(model.usersAndUserGroupsData);
				} catch(e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}

				setIsBusy(false);
			}

			const cancellation = new AbortController();

			fetchData(cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager]
	);

	const context = useMemo(
		(): Context => {
			return {
				canManageUserActiveStatusInWorkspace,
				activeTab,
				selection,
				isMobile
			};
		},
		[
			activeTab,
			canManageUserActiveStatusInWorkspace,
			isMobile,
			selection
		]
	);

	const exportUsersHandler = useCallback(
		(users?: readonly UserGridItem[]): void => {
			const fileContent: string = generateCsvContentForUsers(
				users ?? usersAndUserGroupsData.users
			);

			downloadAsFile(
				fileContent,
				"text/csv",
				"workspace users.csv"
			);
		},
		[usersAndUserGroupsData.users]
	);

	const setUsersActiveStatusHandler = useCallback(
		async (userIds: readonly number[], isActive: boolean): Promise<void> => {
			if (isBusy) {
				return;
			}

			const confirmationResult = await showConfirmationDialog({
				title: localize(
					isActive
						? "_ActivateUsersConfirmationTitle"
						: "_DeactivateUsersConfirmationTitle"
				),
				children:(
					<FormattedText
						text={localize(
							isActive
								? "_ActivateUsersConfirmationDescription"
								: "_DeactivateUsersConfirmationDescription"
						)}
						thingsToReplace={[
							{
								what: "{{Note}}",
								filler: <strong>{localize("_Note")}</strong>
							},
							{
								what: "{{ClickHere}}",
								filler: (
									<Anchor href={BILLING_SUPPORT_ARTICLE_URL} target="_blank">
										{localize("_ClickHere")}
									</Anchor>
								)
							}
						]}
					/>
				),
				confirmText: localize(isActive ? "_MakeActive" : "_MakeInactive"),
				trackingId: isActive
					? "activate-users-confirmation"
					: "deactivate-users-confirmation"
			});

			if (!confirmationResult.wasSuccessful) {
				return;
			}

			setIsBusy(true);
			try {
				const newUsersAndUserGroupData: UsersAndUserGroupsDataModel = isActive
					? await activateUsers(userIds, globalCancellationToken)
					: await deactivateUsers(userIds, globalCancellationToken);

				setUsersAndUserGroupsData(newUsersAndUserGroupData);
				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellationToken,
			isBusy,
			showConfirmationDialog
		]
	);

	const setGroupUsersActiveStatusHandler = useCallback(
		async (groupIds: readonly number[], isActive: boolean): Promise<void> => {
			if (isBusy) {
				return;
			}

			const confirmationResult = await showConfirmationDialog({
				title: localize(
					isActive
						? "_ActivateGroupConfirmationTitle"
						: "_DeactivateGroupConfirmationTitle"
				),
				children: (
					<FormattedText
						text={localize(
							isActive
								? "_ActivateUsersConfirmationDescription"
								: "_DeactivateUsersConfirmationDescription"
						)}
						thingsToReplace={[
							{
								what: "{{Note}}",
								filler: <strong>{localize("_Note")}</strong>
							},
							{
								what: "{{ClickHere}}",
								filler: (
									<Anchor href={BILLING_SUPPORT_ARTICLE_URL} target="_blank">
										{localize("_ClickHere")}
									</Anchor>
								)
							}
						]}
					/>
				),
				confirmText: localize(isActive ? "_MakeActive" : "_MakeInactive"),
				trackingId: isActive
					? "activate-groups-confirmation"
					: "deactivate-groups-confirmation"
			});

			if (!confirmationResult.wasSuccessful) {
				return;
			}

			setIsBusy(true);
			try {
				const newUsersAndUserGroupData: UsersAndUserGroupsDataModel = isActive
					? await activateGroups(groupIds, globalCancellationToken)
					: await deactivateGroups(groupIds, globalCancellationToken);

				setUsersAndUserGroupsData(newUsersAndUserGroupData);
				setIsBusy(false);
			} catch (e) {
				if (isCancelledError(e)) {
					return;
				}

				alertManager.showError(localize("_GeneralErrorMessage"));
				setIsBusy(false);
			}
		},
		[
			alertManager,
			globalCancellationToken,
			isBusy,
			showConfirmationDialog
		]
	);

	const resolveUnanimousLicense = useCallback(
		(audience: SetUserLicenseAudience): UserLicense | undefined => {
			if (!("userIds" in audience)) {
				return undefined;
			}

			const userIds: readonly number[] = audience.userIds;

			const selectedUsers: UserGridItem[] = usersAndUserGroupsData.users
				.filter(u => userIds.includes(u.id));

			if (selectedUsers.length === 0) {
				return undefined;
			}

			return selectedUsers.every(u => u.userLicense === selectedUsers[0].userLicense)
				? selectedUsers[0].userLicense
				: undefined;
		},
		[usersAndUserGroupsData.users]
	);

	const manageUserLicenseHandler = useCallback(
		async (audience: SetUserLicenseAudience): Promise<void> => {
			const result: TryResult<void> = await openChangeUserLicensesDialog({
				audience,
				notAvailableLicense: resolveUnanimousLicense(audience)
			});

			if (!result.wasSuccessful) {
				return;
			}

			pushMessage({
				text: localize("_ChangesSaved"),
				type: "success"
			});

			reloadCurrentPage();
		},
		[openChangeUserLicensesDialog, resolveUnanimousLicense]
	);

	const deselectHandler = useCallback(
		(activeTab: ActiveTab): void => {
			setSelection(x => ({
				userGroups: activeTab === ActiveTab.UserGroup ? [] : x.userGroups,
				users: activeTab === ActiveTab.User ? [] : x.users,
			}));
		},
		[]
	);

	const handlers = useMemo(
		(): ContextualActionsHandlers => {
			return {
				setUsersActiveStatus: setUsersActiveStatusHandler,
				setGroupUsersActiveStatus: setGroupUsersActiveStatusHandler,
				changeLicense: (userIds) => manageUserLicenseHandler({
					userIds
				}),
				changeLicensesForGroups: (userGroupIds) => manageUserLicenseHandler({
					userGroupIds
				}),
				exportToCsv: exportUsersHandler
			};
		},
		[
			exportUsersHandler,
			manageUserLicenseHandler,
			setGroupUsersActiveStatusHandler,
			setUsersActiveStatusHandler
		]
	);

	const selectionActions = useMemo(
		(): readonly LicensingAction[] => getSelectionActions({ deselect: deselectHandler }),
		[deselectHandler]
	);

	const contextualActions = useMemo(
		(): readonly LicensingAction[] => listContextualActions(handlers),
		[handlers]
	);

	const defaultActions = useMemo(
		(): readonly LicensingAction[] => {
			return listDefaultActions(exportUsersHandler);
		},
		[exportUsersHandler]
	);

	const hasSelection = useMemo(
		(): boolean => {
			const { userGroups, users } = context.selection;

			return (context.activeTab === ActiveTab.UserGroup && userGroups.length !== 0)
				|| (context.activeTab === ActiveTab.User && users.length !== 0);
		},
		[
			context.activeTab,
			context.selection
		]
	);

	const topBarActions = useMemo(
		(): readonly LicensingAction[] => {
			return buildActionsForTopBar<Context, LicensingAction>(
				{
					selectionActions,
					contextualActions,
					defaultActions,
				},
				hasSelection,
				isResponsive,
				false
			);
		},
		[
			selectionActions,
			contextualActions,
			defaultActions,
			hasSelection,
			isResponsive
		]
	);

	return (
		<>
			<SettingsPage
				hasBusyIndicator={isBusy}
				actions={topBarActions}
				actionContext={context}
				title={localize("_Licensing")}
				hasSelection={hasSelection}
			>
				{canManageUserActiveStatusInWorkspace === CanManageUserActivateStatusKind.IsManagedInAzureAD && !wasAlertDismissed && (
					<div className={styles.alertBarContainer}>
						<AlertBar
							kind="info"
							isInline
							isDismissable
							onDismiss={() => setWasAlertDismissed(true)}
						>
							{localize("_UserActiveStatusManagedInADExplanation")}
						</AlertBar>
					</div>
				)}

				<SettingsPage.Section height="stretch">
					<UserAndUserGroupOverviews
						selection={selection}
						onSelectionChange={setSelection}
						data={usersAndUserGroupsData}
						activeTab={activeTab}
						onActiveTabChanged={setActiveTab}
						userGroupUserColumnToShow="active-direct"
					/>
				</SettingsPage.Section>
				<AiAssistantFabSpacing />
			</SettingsPage>

			{confirmationDialog}
			{changeUserLicensesDialog}
		</>
	);
};
Page.displayName = "Page";

type Context = {
	canManageUserActiveStatusInWorkspace: CanManageUserActivateStatusKind;
	selection: UserAndUserGroupSelection;
	activeTab: ActiveTab;
	isMobile: boolean;
};

type LicensingAction = ToolbarAction<Context>;

function listDefaultActions(
	exportToCsv: () => void
): readonly LicensingAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "export",
			title: localize("_ExportAllToCsv"),
			icon: "file-export",
			canExecute: (c) => c.activeTab === ActiveTab.User
				&& c.selection
					.users
					.length === 0,
			execute: () => exportToCsv()
		}
	];
}

type SelectionActionsHandlers = {
	deselect: (activeTab: ActiveTab) => void;
};

function getSelectionActions(
	handlers: SelectionActionsHandlers
): readonly LicensingAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "deselect-all",
			title: (c) => {
				const { selection } = c;
				const activeSelection: readonly UserGroupOverviewModel[] | readonly UserGridItem[] =
					c.activeTab === ActiveTab.UserGroup
						? selection.userGroups
						: selection.users;

				return deselectAllTitle(activeSelection);
				},
			icon: "circle-xmark",
			canExecute: (c) => {
				const { selection } = c;
				const activeSelection: readonly UserGroupOverviewModel[] | readonly UserGridItem[] =
					c.activeTab === ActiveTab.UserGroup
						? selection.userGroups
						: selection.users;

				return activeSelection.length > 0;
			},
			execute: (c) => handlers.deselect(c.activeTab)
		},
		{
			kind: ActionKind.Divider,
			name: "deselect-all-divider",
			canExecute: (c) => {
				const { selection } = c;
				const activeSelection: readonly UserGroupOverviewModel[] | readonly UserGridItem[] =
					c.activeTab === ActiveTab.UserGroup
						? selection.userGroups
						: selection.users;

				return !c.isMobile
					&& activeSelection.length > 0;
			}
		}
	];
}

type ContextualActionsHandlers = {
	setUsersActiveStatus: (userIds: readonly number[], isActive: boolean) => void;
	setGroupUsersActiveStatus: (groupIds: readonly number[], isActive: boolean) => void;
	changeLicense: (userIds: readonly number[]) => void;
	changeLicensesForGroups: (userGroupIds: readonly number[]) => void;
	exportToCsv: (users?: readonly UserGridItem[]) => void;
};

function listContextualActions(handlers: ContextualActionsHandlers): readonly LicensingAction[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "make-users-active",
			title: localize("_MakeActive"),
			icon: "user-check",
			canExecute: (c) => c.canManageUserActiveStatusInWorkspace === CanManageUserActivateStatusKind.Allowed
				&& c.activeTab === ActiveTab.User
				&& c.selection
					.users
					.some(x => !x.isActive),
			execute: (c) => handlers.setUsersActiveStatus(
				c.selection
					.users
					.map(x => x.id),
				true
			)
		},
		{
			kind: ActionKind.Regular,
			name: "make-users-inactive",
			title: localize("_MakeInactive"),
			icon: "user-xmark",
			canExecute: (c) => c.canManageUserActiveStatusInWorkspace === CanManageUserActivateStatusKind.Allowed
				&& c.activeTab === ActiveTab.User
				&& c.selection
					.users
					.some(x => x.isActive),
			execute: (c) => handlers.setUsersActiveStatus(
				c.selection
					.users
					.map(x => x.id),
				false
			)
		},
		{
			kind: ActionKind.Regular,
			name: "change-license",
			title: localize("_ChangePackage"),
			icon: "arrow-right-arrow-left",
			canExecute: (c) => c.activeTab === ActiveTab.User
				&& c.selection
					.users
					.length > 0,
			execute: (c) => handlers.changeLicense(
				c.selection
					.users
					.map(x => x.id)
			)
		},
		{
			kind: ActionKind.Regular,
			name: "make-group-users-active",
			title: localize("_MakeDirectUsersActive"),
			icon: "user-check",
			canExecute: (c) => c.canManageUserActiveStatusInWorkspace === CanManageUserActivateStatusKind.Allowed
				&& c.activeTab === ActiveTab.UserGroup
				&& c.selection
					.userGroups
					.some(x => x.numberOfActiveNonDeletedDirectUsers < x.numberOfNonDeletedDirectUsers),
			execute: (c) => handlers.setGroupUsersActiveStatus(
				c.selection
					.userGroups
					.map(x => x.id),
				true
			)
		},
		{
			kind: ActionKind.Regular,
			name: "make-group-users-inactive",
			title: localize("_MakeDirectUsersInactive"),
			icon: "user-xmark",
			canExecute: (c) => c.canManageUserActiveStatusInWorkspace === CanManageUserActivateStatusKind.Allowed
				&& c.activeTab === ActiveTab.UserGroup
				&& c.selection
					.userGroups
					.some(x => x.numberOfActiveNonDeletedDirectUsers > 0),
			execute: (c) => handlers.setGroupUsersActiveStatus(
				c.selection
					.userGroups
					.map(x => x.id),
				false
			)
		},
		{
			kind: ActionKind.Regular,
			name: "change-licenses-for-group-users",
			title: localize("_ChangePackagesForDirectUsers"),
			icon: "arrow-right-arrow-left",
			canExecute: (c) => c.activeTab === ActiveTab.UserGroup
				&& c.selection
					.userGroups
					.length > 0,
			execute: (c) => handlers.changeLicensesForGroups(
				c.selection
					.userGroups
					.map(x => x.id)
			)
		},
		{
			kind: ActionKind.Regular,
			name: "export-selection",
			title: localize("_ExportSelectionToCsv"),
			icon: "file-export",
			canExecute: (c) => c.activeTab === ActiveTab.User
				&& c.selection
					.users
					.length > 0,
			execute: (c) => handlers.exportToCsv(c.selection.users)
		}
	];
}

function generateCsvContentForUsers(users: readonly UserGridItem[]): string {
	const headers: readonly string[] = [
		localize("_FirstName"),
		localize("_LastName"),
		localize("_Email"),
		localize("_Username"),
		localize("_Status"),
		localize("_Package"),
		localize("_GuestUser"),
		localize("_Administrator"),
	];

	const data: readonly (readonly string[])[] = users
		.map((x): readonly string[] => [
			x.firstName ?? "",
			x.lastName ?? "",
			x.primaryEmail ?? "",
			x.userPrincipalName,
			x.isActive ? localize("_Active") : localize("_Inactive"),
			x.isActive && isNil(x.deletionDate)
				? getUserLicenseName(x.userLicense)
				: "",
			`${x.isGuest}`,
			`${x.isAdmin}`
		]);

	return formatDataAsCsv(
		headers,
		data
	);
}

const DEFAULT_USERS_AND_USER_GROUPS_DATA: UsersAndUserGroupsDataModel = {
	users: [],
	userGroups: []
};

export const userLicensingManagementRouting = {
	page: "/UserLicensingManagement"
};
